import { ApiClient } from "./api.ts";
import { Plugin, RulePlugin } from "../models/types.ts";

export class PluginService {
    private static instance: PluginService;
    private api = ApiClient.getInstance();

    private constructor() { }

    public static getInstance(): PluginService {
        if (!PluginService.instance) {
            PluginService.instance = new PluginService();
        }
        return PluginService.instance;
    }

    public async listPlugins(orgId: string): Promise<Plugin[]> {
        return this.api.request<Plugin[]>(`/organization/${orgId}/plugins`) as Promise<Plugin[]>;
    }

    public async getPlugin(id: string): Promise<Plugin> {
        return this.api.request<Plugin>(`/plugin/${id}`) as Promise<Plugin>;
    }

    public async createPlugin(plugin: Plugin): Promise<Plugin> {
        return this.api.request<Plugin>("/plugins", {
            method: "POST",
            body: JSON.stringify(plugin)
        }) as Promise<Plugin>;
    }

    public async updatePlugin(plugin: Plugin): Promise<Plugin> {
        return this.api.request<Plugin>(`/plugin/${plugin.id}`, {
            method: "PATCH",
            body: JSON.stringify(plugin)
        }) as Promise<Plugin>;
    }

    public async deletePlugin(id: string): Promise<void> {
        await this.api.request(`/plugin/${id}`, {
            method: "DELETE"
        });
    }

    public async getRulePlugins(ruleId: string): Promise<Plugin[]> {
        return this.api.request<Plugin[]>(`/rule/${ruleId}/plugins`) as Promise<Plugin[]>;
    }

    public async attachPluginToRule(ruleId: string, pluginId: string, order: number): Promise<void> {
        await this.api.request(`/rule/${ruleId}/plugins`, {
            method: "PUT",
            body: JSON.stringify({ plugin_id: pluginId, execution_order: order })
        });
    }

    public async detachPluginFromRule(ruleId: string, pluginId: string): Promise<void> {
        await this.api.request(`/rule/${ruleId}/plugins/${pluginId}`, {
            method: "DELETE"
        });
    }

    public async updateRulePluginOrder(ruleId: string, pluginOrders: Record<string, number>): Promise<void> {
        await this.api.request(`/rule/${ruleId}/plugins/order`, {
            method: "PATCH",
            body: JSON.stringify({ plugin_orders: pluginOrders })
        });
    }

    public async getDomainPlugins(domainId: string): Promise<Plugin[]> {
        return this.api.request<Plugin[]>(`/domain/${domainId}/plugins`) as Promise<Plugin[]>;
    }

    public async attachPluginToDomain(domainId: string, pluginId: string, direction: string, order: number): Promise<void> {
        await this.api.request(`/domain/${domainId}/plugins`, {
            method: "PUT",
            body: JSON.stringify({ plugin_id: pluginId, direction, execution_order: order })
        });
    }

    public async detachPluginFromDomain(domainId: string, pluginId: string): Promise<void> {
        await this.api.request(`/domain/${domainId}/plugins/${pluginId}`, {
            method: "DELETE"
        });
    }

    public async updateDomainPluginOrder(domainId: string, pluginOrders: Record<string, number>): Promise<void> {
        await this.api.request(`/domain/${domainId}/plugins/order`, {
            method: "PATCH",
            body: JSON.stringify({ plugin_orders: pluginOrders })
        });
    }

    public async simulatePlugin(simulation: any): Promise<any> {
        return this.api.request(`/plugins/simulate`, {
            method: "POST",
            body: JSON.stringify(simulation),
        });
    }
}
